"use client";
import FactoidBar from "@/components/CourseContent/ContentRefresh/FactoidBar";
import { ProgramDetailContentProps } from "../types";
import InstructorList from "@/components/CourseContent/ContentRefresh/InstructorList";
import ProgramContent from "@/components/CourseContent/ContentOverview";
import WhatYoullLearn from "@/components/CourseContent/ContentRefresh/WhatYoullLearn";
import {
  fullWidthContainer,
  mainContent,
  mainContentSideRail,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import styles from "@/components/CourseContent/styles/contentHero.module.css";
import { getAllInstructorsFromCourse } from "@/lib/instructor/getAllInstructorsFromCourse";
import MidPageCTA from "./MidPageCTA";
import ProgramHeader from "@/components/ProgramDetailLayout/ProgramDetailRefresh/ProgramHeader";
import getProgramDuration from "@/lib/program/utils/getProgramDuration";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import getProgramFromSubject from "@/components/ProgramDetailLayout/ProgramDetailRefresh/utils/getSubjectFromProgram";
import CategoryCTA from "./CategoryCTA";
import ProgramRelatedContent from "@/components/ProgramDetailLayout/ProgramDetailRefresh/ProgramRelatedContent";
import { CourseVideo } from "@/components/CourseContent/ContentOverview/types";
import ProgramEnroll from "./ProgramEnroll";
import IndustryInsights from "./IndustryInsights";
import checkRTL from "@/utils/functions/checkRTL";
import sortBySortValue from "@/utils/functions/sortBySortValue";
import ProgramSummaryPathway from "./ProgramSummaryPathway";
import { SITE_URL } from "@/lib/program/utils/seo/generateProgramSeo";
import SocialShare from "@/components/CourseContent/ContentRefresh/SocialShare";
import ProgramEndorsements from "@/components/ProgramDetailLayout/ProgramDetailRefresh/ProgramEndorsements";
import OFACMessage from "../../CourseContent/ContentRefresh/OFACmessage";
import ProgramStickyCTA from "./ProgramStickyCTA";
import useProgramRedesignTraffic from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import ProgramHeroSideRailContainer from "../ProgramHeroSideRail/ProgramHeroSideRailContainer";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import useSideRailComponentList from "../ProgramHeroSideRail/SideRailContent/useSideRailComponentList";
import { SideRailComponentProps } from "@/monarch/rules/programRedesignTraffic/types";
import { RedesignedTrackComparison } from "@/components/Certificate/constants/enums";
import CertificateEmphasisTabs from "@/components/Certificate/CertificateEmphasisTabs";
import ProgramEnrollButton from "./ProgramEnrollButton";
import MobileContent from "../ProgramHeroSideRail/SideRailContent/MobileContent";
import ProgramPromoBanner from "./ProgramPromoBanner";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";

function hasSideRailVideoPlayerComponent(components: SideRailComponentProps[]) {
  return !!components.find((componentItem) => {
    return componentItem.component === "videoPlayer";
  });
}

export default function ProgramDetailContent({
  programData,
  courses,
  pricingData,
  skus,
  programIsEnrollable,
}: ProgramDetailContentProps) {
  const {
    sectionsOrder,
    headerCta,
    isLoading,
    factoidsBar,
    showSideRail,
    sideRailHeaderComponents,
    sideRailBodyComponents,
    sideRailCollapsedHeaderComponents,
    sideRailCollapsedBodyComponents,
    sideRailMobileHeaderComponents,
    sideRailMobileBodyComponents,
    hidePrice,
    emphasisModuleVersion,
    information,
    stats,
    testimonial,
    tabsOrder,
    pathInfo,
  } = useProgramRedesignTraffic();

  const sideRailComponentList = useSideRailComponentList({
    programData,
    courses,
    pricingData,
    skus,
    programIsEnrollable,
  });

  //If the video is live in the side rail, then it shouldn’t be in the content.
  const showingSideRailVideo =
    !!showSideRail &&
    hasSideRailVideoPlayerComponent(
      (sideRailHeaderComponents || [])
        .concat(sideRailBodyComponents || [])
        .concat(sideRailCollapsedHeaderComponents || [])
        .concat(sideRailCollapsedBodyComponents || [])
        .concat(sideRailMobileHeaderComponents || [])
        .concat(sideRailMobileBodyComponents || []),
    );

  const { expectedLearningItems } = programData;

  const programOverviewVideo =
    programData.video.length > 0
      ? (programData.video[0] as CourseVideo)
      : undefined;
  if (programOverviewVideo) {
    // Program videos do not have a thumbnail image, this one was provided as fallback by the design team
    programOverviewVideo.image = {
      src: "/bg-images/video-player-thumbnail.jpg",
    };
  }
  const programSubject = getProgramFromSubject(programData);
  programData.video.length > 0
    ? (programData.video[0] as CourseVideo)
    : undefined;
  const instructors = getAllInstructorsFromCourse(courses);

  const formattedInsights = programData.jobOutlookItems.map((item) => ({
    value: item.value ?? "",
  }));

  const duration = courses && getProgramDuration(courses);
  const filteredExpectedLearningItems = sortBySortValue(
    expectedLearningItems.filter((item) => item.value),
  );
  const areLearningItemsRTL = checkRTL(
    filteredExpectedLearningItems.map((item) => item.value).join(""),
  );

  const componentList = [
    {
      key: "promoBanner",
      renderer: () => {
        return programIsEnrollable &&
          programData.programEligibleForOneClickPurchase ? (
          <FeatureFlagComponentResolverClient
            flagKey="NEXT_PUBLIC_FEATURE_PROGRAM_PROMO_BANNER"
            variants={{
              legacy: null,
              test: (
                <ProgramPromoBanner
                  programData={programData}
                  courses={courses}
                  skus={skus}
                />
              ),
            }}
            fallback="legacy"
          />
        ) : null;
      },
    },
    {
      key: "whatYoullLearn",
      renderer: () => {
        return filteredExpectedLearningItems &&
          filteredExpectedLearningItems.length > 0 ? (
          <WhatYoullLearn
            key="whatYoullLearn"
            title="What you'll learn"
            learningItems={filteredExpectedLearningItems}
            areLearningItemsRTL={areLearningItemsRTL}
          />
        ) : null;
      },
    },
    {
      key: "programOverview",
      renderer: () => {
        return (
          <ProgramContent
            key="programOverview"
            title="Program overview"
            fullDescription={programData.overview}
            video={programOverviewVideo}
            isVideoInSideRail={programOverviewVideo && showingSideRailVideo} //video should not show when the side bar is displayed
          />
        );
      },
    },
    {
      key: "emphasisModuleVersion",
      renderer: () => {
        return emphasisModuleVersion &&
          emphasisModuleVersion !== RedesignedTrackComparison.Hidden ? (
          <div className={maxWidthContainer}>
            {emphasisModuleVersion === RedesignedTrackComparison.Tabbed ? (
              <CertificateEmphasisTabs
                key="certificateEmphasisTabs"
                information={information}
                stats={stats}
                testimonial={testimonial}
                tabOrder={tabsOrder}
                hidePrice={hidePrice}
                pathInfo={pathInfo}
                ctaElementRenderer={(tabId: string, ctaClassName: string) => {
                  return (
                    <ProgramEnrollButton
                      programData={programData}
                      courses={courses}
                      skus={skus}
                      location={`cert-module-${tabId}`}
                      enableTracking={true}
                    />
                  );
                }}
              />
            ) : null}
          </div>
        ) : null;
      },
    },
    {
      key: "programSummaryPathway",
      renderer: () => {
        return (
          <ProgramSummaryPathway
            key="programSummaryPathway"
            courses={courses}
          />
        );
      },
    },
    {
      key: "midPageCTA",
      renderer: () => {
        return programData.programEligibleForOneClickPurchase &&
          programIsEnrollable ? (
          <MidPageCTA
            key="midPageCTA"
            programData={programData}
            courses={courses}
            skus={skus}
            className={showSideRail ? "hidden md:flex xl:hidden" : ""}
          />
        ) : null;
      },
    },
    {
      key: "instructorList",
      renderer: () => {
        return (
          <InstructorList
            key="instructorList"
            instructors={instructors}
            showSingleColumn={showSideRail}
          />
        );
      },
    },
    {
      key: "industryInsights",
      renderer: () => {
        return formattedInsights?.length > 0 ? (
          <IndustryInsights
            key="industryInsights"
            insights={formattedInsights}
          />
        ) : null;
      },
    },
    {
      key: "programEndorsements",
      renderer: () => {
        return programData.corporateEndorsements?.length > 0 ? (
          <ProgramEndorsements
            key="programEndorsements"
            corporateEndorsements={programData.corporateEndorsements}
          />
        ) : null;
      },
    },
    {
      key: "creditPathways",
      renderer: () => {
        return programData.creditRedemptionOverview ? (
          <ProgramContent
            key="creditPathways"
            title="Certificate & credit pathways"
            fullDescription={programData.creditRedemptionOverview}
          />
        ) : null;
      },
    },
    {
      key: "programEnroll",
      renderer: () => {
        return programData.programEligibleForOneClickPurchase &&
          programIsEnrollable &&
          pricingData &&
          (pricingData.originalPrice || pricingData.discountedPrice) ? (
          <ProgramEnroll
            key="programEnroll"
            courses={courses || []}
            programData={programData}
            duration={duration || null}
            skus={skus}
            pricingData={pricingData}
          />
        ) : null;
      },
    },
    {
      key: "programRelatedContent",
      renderer: () => {
        return (
          <ProgramRelatedContent
            key="programRelatedContent"
            programSubject={programSubject}
          />
        );
      },
    },
  ];

  const headerContainerClasses = showSideRail
    ? "xl:flex xl:gap-6 relative"
    : "";
  const bodyContainerClasses = showSideRail ? "xl:w-[1280px] xl:mx-auto" : "";

  return (
    <>
      <div
        className={`${
          headerCta?.bgColor !== "light" ? styles.contentHero : "bg-putty-100"
        } pb-12 pt-20 sm:py-12`}
      >
        <div className={`${maxWidthContainer} ${fullWidthContainer} gap-8`}>
          <Breadcrumbs
            variant={headerCta?.bgColor === "light" ? "dark" : "light"}
            path={programData?.marketingUrl || ""}
            pageName={programData.title}
            containerClassName="not-prose"
            linksToShowOnSmallScreen={1}
          />
          <div className={headerContainerClasses}>
            <div
              className={`flex flex-col gap-8 ${showSideRail ? "xl:max-w-[824px]" : ""}`}
            >
              {showSideRail && (
                <div className="md:hidden block">
                  <MobileContent componentList={sideRailComponentList} />
                </div>
              )}
              <ProgramHeader
                pricingData={pricingData}
                duration={duration}
                programIsEnrollable={programIsEnrollable}
                skus={skus}
                courses={courses}
                programData={programData}
                showSideRail={showSideRail}
              />
              <div className="w-full" id="program-factoid-container">
                <FactoidBar
                  courseData={courses}
                  isFactoidsBarFieldsLoading={isLoading}
                  monarchFactoidsBar={factoidsBar}
                />
              </div>
            </div>
            <div className="hidden xl:block">
              {showSideRail && (
                <ProgramHeroSideRailContainer
                  programData={programData}
                  courses={courses}
                  pricingData={pricingData}
                  skus={skus}
                  programIsEnrollable={programIsEnrollable}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {programData.programEligibleForOneClickPurchase && programIsEnrollable ? (
        <ProgramStickyCTA
          programData={programData}
          courses={courses}
          pricingData={pricingData}
          skus={skus}
          className={showSideRail ? "xl:hidden" : ""}
        ></ProgramStickyCTA>
      ) : null}
      <div
        className="bg-putty-100 pt-12 md:pt-16 flex flex-col gap-12 sm:gap-16"
        id="program-details-body-container"
      >
        <div className={bodyContainerClasses}>
          <div className={showSideRail ? mainContentSideRail : mainContent}>
            {/* PROMO BANNER GOES HERE */}
            {getSortedComponentsBySectionOrder({
              componentList,
              sectionsOrder,
            })}
          </div>
        </div>
        {programData.hasOfacRestrictions && (
          <OFACMessage type="program" sideRail={showSideRail} />
        )}
        <div>
          <SocialShare
            title="Share this program"
            productUrl={`${SITE_URL}/${programData.marketingUrl}`}
            productName={programData.title}
          />
        </div>
        <div
          className={`bg-putty-300 w-full flex flex-col lg:h-64 justify-center items-center`}
          id="program-category-cta"
        >
          <div className={maxWidthContainer}>
            <CategoryCTA programSubject={programSubject} />
          </div>
        </div>
      </div>
    </>
  );
}
